/**
 * CustomCalendar Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Custom calendar grid for date range selection. Displays single month with date cells,
 * highlighting selected range and disabled dates. Used within CalendarModal.
 * 
 * Features:
 * - Single month calendar grid
 * - Date range selection (start and end dates)
 * - Visual highlighting of selected range
 * - Disabled dates (past dates, unavailable)
 * - Hover preview of date range
 * - European date format (Monday start)
 * - Click to select dates
 * - Responsive sizing
 * 
 * Props:
 * - startDate: Date | null (selected start date)
 * - endDate: Date | null (selected end date)
 * - onDatesChange: (dates: [Date | null, Date | null]) => void
 * - month: Date (month to display)
 * - selectingEnd: boolean (currently selecting end date)
 * - hoverDate: Date | null (hovered date for preview)
 * - onHoverChange: (date: Date | null) => void
 * 
 * Dependencies:
 * - React 19.2.4 (hooks, state)
 * - TailwindCSS 4.1 (responsive styling)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with date range selection
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface CustomCalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  onDatesChange: (dates: [Date | null, Date | null]) => void;
  month?: Date;
  selectingEnd?: boolean;
  hoverDate?: Date | null;
  onHoverChange?: (date: Date | null) => void;
}

export const CustomCalendar: React.FC<CustomCalendarProps> = ({
  startDate,
  endDate,
  onDatesChange,
  month,
  selectingEnd: externalSelectingEnd,
  hoverDate: externalHoverDate,
  onHoverChange
}) => {
  const [currentMonth, setCurrentMonth] = useState(month || new Date());
  const [selectingEnd, setSelectingEnd] = useState(externalSelectingEnd || false);
  const [hoverDate, setHoverDate] = useState<Date | null>(externalHoverDate || null);

  // Update currentMonth when month prop changes
  React.useEffect(() => {
    if (month) {
      setCurrentMonth(month);
    }
  }, [month]);

  // Update internal selectingEnd when external prop changes
  React.useEffect(() => {
    if (externalSelectingEnd !== undefined) {
      setSelectingEnd(externalSelectingEnd);
    }
  }, [externalSelectingEnd]);

  // Update hover date when external prop changes
  React.useEffect(() => {
    if (onHoverChange) {
      setHoverDate(externalHoverDate || null);
    }
  }, [externalHoverDate, onHoverChange]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Adjust for Monday as first day of week
    let startingDayOfWeek = firstDay.getDay() - 1;
    if (startingDayOfWeek === -1) startingDayOfWeek = 6; // Sunday becomes 6

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const isDateSelected = (date: Date) => {
    if (!startDate && !endDate) return false;
    if (!date) return false;
    
    const compareDate = (d1: Date, d2: Date) => {
      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    };

    // Use hoverDate for range preview when selecting end date
    // Only use hoverDate if it's in the same month as the current date being checked
    let displayEndDate = endDate;
    if (selectingEnd && hoverDate && hoverDate > (startDate || date)) {
      // Check if hoverDate is in the same month as the date we're checking
      if (hoverDate.getMonth() === date.getMonth() && hoverDate.getFullYear() === date.getFullYear()) {
        displayEndDate = hoverDate;
      }
    }

    if (startDate && compareDate(date, startDate)) return 'start';
    if (displayEndDate && compareDate(date, displayEndDate)) return 'end';
    if (startDate && displayEndDate && date > startDate && date < displayEndDate) return 'middle';
    return false;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  };

  const isPastDate = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date: Date) => {
    if (isPastDate(date)) return;

    if (!selectingEnd) {
      onDatesChange([date, null]);
      setSelectingEnd(true);
    } else {
      if (date < (startDate || date)) {
        onDatesChange([date, null]);
        setSelectingEnd(true);
      } else {
        onDatesChange([startDate, date]);
        setSelectingEnd(false);
      }
    }
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];
  
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="w-full">
      {/* Day Names - Larger on mobile */}
      <div className="grid grid-cols-7 gap-0.5 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-slate-500 py-2 md:py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid - Larger touch targets on mobile */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square min-h-[44px]" />;
          }

          const selected = isDateSelected(date);
          const today = isToday(date);
          const past = isPastDate(date);

          return (
            <motion.button
              key={date.toISOString()}
              whileHover={{ scale: past ? 1 : 1.05 }}
              whileTap={{ scale: past ? 1 : 0.95 }}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => {
                if (selectingEnd && !past) {
                  const newHoverDate = date;
                  setHoverDate(newHoverDate);
                  if (onHoverChange) {
                    onHoverChange(newHoverDate);
                  }
                }
              }}
              onMouseLeave={() => {
                if (onHoverChange) {
                  onHoverChange(null);
                }
              }}
              disabled={past}
              className={`
                aspect-square min-h-[44px] md:min-h-[32px] rounded-md flex items-center justify-center 
                text-sm md:text-xs font-medium
                transition-all duration-200 relative
                ${past ? 'text-slate-300 cursor-not-allowed' : 'text-slate-700 cursor-pointer'}
                ${today && !selected ? 'border border-slate-300' : ''}
                ${selected === 'start' ? 'bg-brand-ocean text-white rounded-l-md' : ''}
                ${selected === 'end' ? 'bg-brand-ocean text-white rounded-r-md' : ''}
                ${selected === 'middle' ? 'bg-brand-ocean/10 text-brand-ocean' : ''}
                ${!selected && !past && !today ? 'hover:bg-slate-100' : ''}
              `}
            >
              {date.getDate()}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};
